import axiosRestaurantInstance from '../restaurant/AxiosRestaurantInstance';
import { GROCERY_API_PATHS, RESTAURANT_API_PATHS } from '../constants/apiPaths';

function authHeaders(extra = {}) {
  const token = localStorage.getItem('Token');
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

function getActorId() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    return Number(user?.userId ?? user?.User_Id ?? user?.user_id ?? 0) || 0;
  } catch {
    return 0;
  }
}

/** Normalize to API entity type: RESTAURANT | STORE | PHARMA | … */
export function normalizeBusinessEntityType(entityType) {
  const key = String(entityType || '').trim().toUpperCase();
  if (['RST', 'RESTAURANT', 'KITCHEN'].includes(key)) return 'RESTAURANT';
  if (['STR', 'STORE', 'GROCERY', 'SUPERMARKET'].includes(key)) return 'STORE';
  if (['PHARMA', 'PHARMACY'].includes(key)) return 'PHARMA';
  if (['ORG', 'ORGANIZATION'].includes(key)) return 'ORGANIZATION';
  return key || 'RESTAURANT';
}

export const SOCIAL_PLATFORMS = [
  'instagram',
  'facebook',
  'youtube',
  'whatsapp',
  'twitter',
  'linkedin',
];

export async function fetchBusinessProfileMedia(entityType, entityId) {
  const type = normalizeBusinessEntityType(entityType);
  const response = await axiosRestaurantInstance.get(
    RESTAURANT_API_PATHS.businessProfileMedia(entityId, type),
    { headers: authHeaders() },
  );
  return response.data ?? {};
}

export async function uploadBusinessProfilePhoto(entityType, entityId, imageFile) {
  const type = normalizeBusinessEntityType(entityType);
  const formData = new FormData();
  formData.append('image', imageFile);
  formData.append('uploadedById', String(getActorId()));
  const response = await axiosRestaurantInstance.post(
    RESTAURANT_API_PATHS.businessProfileMediaImages(entityId, type),
    formData,
    { headers: authHeaders({ 'Content-Type': 'multipart/form-data' }) },
  );
  return response.data ?? {};
}

export async function uploadBusinessLogo(entityType, entityId, imageFile) {
  const type = normalizeBusinessEntityType(entityType);
  const formData = new FormData();
  formData.append('logo', imageFile);
  formData.append('uploadedById', String(getActorId()));
  const response = await axiosRestaurantInstance.post(
    RESTAURANT_API_PATHS.businessProfileMediaLogo(entityId, type),
    formData,
    { headers: authHeaders({ 'Content-Type': 'multipart/form-data' }) },
  );
  return response.data ?? {};
}

export async function saveBusinessYoutubeLinks(entityType, entityId, youtubeUrls) {
  const type = normalizeBusinessEntityType(entityType);
  const response = await axiosRestaurantInstance.put(
    RESTAURANT_API_PATHS.businessProfileMediaYoutube(entityId, type),
    { youtubeUrls, updatedById: getActorId(), type },
    { headers: authHeaders() },
  );
  return response.data ?? {};
}

export async function saveBusinessSocialLinks(entityType, entityId, socialLinks) {
  const type = normalizeBusinessEntityType(entityType);
  const response = await axiosRestaurantInstance.put(
    RESTAURANT_API_PATHS.businessProfileMediaSocial(entityId, type),
    { socialLinks, updatedById: getActorId(), type },
    { headers: authHeaders() },
  );
  return response.data ?? {};
}

export async function saveBusinessTagline(entityType, entityId, tagline) {
  const type = normalizeBusinessEntityType(entityType);
  const response = await axiosRestaurantInstance.put(
    RESTAURANT_API_PATHS.businessProfileMediaTagline(entityId, type),
    { tagline: String(tagline || '').trim(), updatedById: getActorId(), type },
    { headers: authHeaders() },
  );
  return response.data ?? {};
}

export async function deleteBusinessProfileMedia(entityType, entityId, mediaId) {
  const type = normalizeBusinessEntityType(entityType);
  const response = await axiosRestaurantInstance.delete(
    RESTAURANT_API_PATHS.businessProfileMediaById(entityId, type, mediaId),
    {
      headers: authHeaders(),
      data: { updatedById: getActorId() },
    },
  );
  return response.data ?? {};
}

export async function fetchBusinessPublicProfile(entityType, entityId) {
  const type = normalizeBusinessEntityType(entityType);
  const response = await axiosRestaurantInstance.get(
    RESTAURANT_API_PATHS.businessPublicProfile(entityId, type),
    { headers: authHeaders() },
  );
  return response.data ?? {};
}

export async function fetchBusinessProfileItems(entityType, entityId) {
  const type = normalizeBusinessEntityType(entityType);
  if (type === 'STORE') {
    const response = await axiosRestaurantInstance.get(
      GROCERY_API_PATHS.storeInventoryItemsByStoreId(entityId),
      { headers: authHeaders() },
    );
    const rows = Array.isArray(response.data)
      ? response.data
      : response.data?.items || response.data?.inventoryItems || [];
    return rows.slice(0, 12).map((row) => ({
      id: row.inventoryItemId ?? row.itemId ?? row.id,
      name: row.itemName ?? row.name ?? row.productName ?? 'Item',
      imageUrl: row.imageUrl ?? row.image ?? row.Image ?? null,
      price: row.price ?? row.sellingPrice ?? row.mrp ?? null,
    }));
  }

  const response = await axiosRestaurantInstance.get(
    RESTAURANT_API_PATHS.restaurantFoodItemsByRestaurantId(entityId),
    {
      headers: authHeaders(),
      params: { salesChannel: 'online' },
    },
  );
  const body = response.data;
  let flat = [];
  if (Array.isArray(body)) {
    flat = body;
  } else if (body && typeof body === 'object') {
    Object.values(body).forEach((list) => {
      if (Array.isArray(list)) flat.push(...list);
    });
  }
  return flat.slice(0, 12).map((row) => ({
    id: row.foodItemId ?? row.id,
    name: row.foodItemName ?? row.name ?? row.itemName ?? 'Item',
    imageUrl: row.imageUrl ?? row.image ?? row.Image ?? null,
    price: row.price ?? row.onlinePrice ?? null,
  }));
}

export const fetchRestaurantProfileMedia = (id) => fetchBusinessProfileMedia('RESTAURANT', id);
export const uploadRestaurantKitchenPhoto = (id, file) =>
  uploadBusinessProfilePhoto('RESTAURANT', id, file);
export const saveRestaurantYoutubeLinks = (id, urls) =>
  saveBusinessYoutubeLinks('RESTAURANT', id, urls);
export const deleteRestaurantProfileMedia = (id, mediaId) =>
  deleteBusinessProfileMedia('RESTAURANT', id, mediaId);

export const fetchStoreProfileMedia = (id) => fetchBusinessProfileMedia('STORE', id);
export const uploadStoreKitchenPhoto = (id, file) =>
  uploadBusinessProfilePhoto('STORE', id, file);
export const saveStoreYoutubeLinks = (id, urls) => saveBusinessYoutubeLinks('STORE', id, urls);
export const deleteStoreProfileMedia = (id, mediaId) =>
  deleteBusinessProfileMedia('STORE', id, mediaId);
